import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../store/auth-store';
import { trackEmailVerified } from '../../utils/analytics';
import {
  leggiInvitoInSospeso,
  dimenticaInvitoInSospeso,
} from '../../utils/pending-tournament-invite';
import { log } from '../../utils/logger';
import './AuthModal.css';

interface VerifyEmailPageProps {
  token: string;
  onComplete: () => void;
  /** Riporta alla pagina del torneo, come farebbe il link d'invito. */
  onTornaAlTorneo: (id: string, gettone: string | null) => void;
}

function VerifyEmailPage({ token, onComplete, onTornaAlTorneo }: VerifyEmailPageProps) {
  const { t } = useTranslation();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');
  const hasVerified = useRef(false);
  const verificaEmail = useAuthStore((state) => state.verificaEmail);

  // La schermata cambia a ogni resa; l'effetto gira una volta sola e deve
  // vedere le funzioni di adesso, non quelle del primo montaggio.
  const verificaRef = useRef(verificaEmail);
  verificaRef.current = verificaEmail;
  const tornaRef = useRef(onTornaAlTorneo);
  tornaRef.current = onTornaAlTorneo;

  useEffect(() => {
    // Auto-verify on mount: clicking the email link is enough, no extra click needed.
    // Guard against StrictMode's double-invoke in dev to avoid a duplicate POST.
    if (hasVerified.current) return;
    hasVerified.current = true;

    const handleVerify = async () => {
      log.info('VerifyEmailPage', 'Verification attempt');
      try {
        /*
         * Non e' piu' solo una verifica: qui si ENTRA. Il gettone del link e' la
         * prova d'identita' che il muro della verifica chiedeva, e il magazzino
         * registra la sessione che il server consegna.
         */
        await verificaRef.current(token);
        setStatus('success');
        log.info('VerifyEmailPage', 'Verification success');
        trackEmailVerified();

        /*
         * E se il giro era cominciato da un invito a un torneo, si torna LI'.
         * Il ricordo si spende una volta sola: lasciarlo in memoria dirotterebbe
         * anche il prossimo giro, magari di un'altra persona sullo stesso
         * dispositivo. In caso di errore invece resta, perche' chi si fa mandare
         * un nuovo link deve ritrovare la strada.
         */
        const invito = leggiInvitoInSospeso();
        if (invito) {
          dimenticaInvitoInSospeso();
          setMessage(t('auth.verifyEmail.successAndBack'));
          log.info('VerifyEmailPage', 'Returning to pending tournament invite', { id: invito.id });
          tornaRef.current(invito.id, invito.token);
          return;
        }
        setMessage(t('auth.verifyEmail.success'));
      } catch (error) {
        setStatus('error');
        setMessage(error instanceof Error ? error.message : t('auth.verifyEmail.error'));
        log.error('VerifyEmailPage', 'Verification error', error);
      }
    };

    handleVerify();
  }, [token, t]);

  return (
    <div className="verify-email-page">
      <div className="verify-email-card">
        <h2>{t('auth.verifyEmail.pageTitle')}</h2>

        {status === 'loading' && (
          <>
            <div className="verify-email-spinner" />
            <p>{t('auth.verifyEmail.verifying')}</p>
          </>
        )}

        {status === 'success' && (
          <>
            <p className="verify-email-success">{message}</p>
            {/*
              * «Continua», non «Accedi»: la sessione e' gia' aperta, e offrire
              * un accesso a chi e' appena entrato lo rimanderebbe a fare una
              * cosa che ha gia' fatto.
              */}
            <button
              className="auth-submit"
              onClick={onComplete}
              style={{ marginTop: '16px' }}
            >
              {t('common.continue')}
            </button>
          </>
        )}

        {status === 'error' && (
          <>
            <p className="verify-email-error">{message}</p>
            <button
              className="auth-submit"
              onClick={onComplete}
              style={{ marginTop: '16px' }}
            >
              {t('common.back')}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default VerifyEmailPage;
